import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const getExportCellValue = (row, col, index) => {
  if (typeof col.render === "function") {
    const rendered = col.render(row, index);
    if (
      rendered === null ||
      rendered === undefined ||
      typeof rendered === "object"
    ) {
      return row[col.field] ?? "N/A";
    }
    return rendered;
  }

  return row[col.field] ?? "N/A";
};

const mapRowsForExport = (data = [], columns = []) =>
  data.map((row, index) => [
    index + 1,
    ...columns.map((col) => getExportCellValue(row, col, index)),
  ]);

export const ExportInspectionReport = ({
  reportData = [],
  reportTitle = "Inspection Report",
  columns = [],
  fileName = "InspectionReport",
  loadExportData,
}) => {
  const exportColumns = ["S.No", ...columns.map((col) => col.label)];

  const resolveExportData = async () => {
    if (typeof loadExportData === "function") {
      const data = await loadExportData();
      return Array.isArray(data) ? data : reportData;
    }

    return reportData;
  };

  const exportToExcel = async () => {
    const rows = await resolveExportData();
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(reportTitle);
    const boldFont = { bold: true };

    const currentDate = new Date();
    const formattedDate = `${currentDate.getDate()}-${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`;
    const formattedTime = `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;
    const downloadDateTime = `${reportTitle} Generated on: ${formattedDate}, ${formattedTime}`;

    worksheet.addRow(["", "", downloadDateTime, ""]);
    worksheet.mergeCells(1, 2, 1, exportColumns.length);
    worksheet.getCell(1, 2).value = downloadDateTime;
    worksheet.getCell(1, 2).font = boldFont;
    worksheet.getCell(1, 2).alignment = { horizontal: "center" };

    worksheet.addRow(["", "", `${reportTitle} List`, ""]);
    worksheet.mergeCells(2, 2, 2, exportColumns.length);
    worksheet.getCell(2, 2).value = `${reportTitle} List`;
    worksheet.getCell(2, 2).font = boldFont;
    worksheet.getCell(2, 2).alignment = { horizontal: "center" };

    const tableHeaderRow = worksheet.addRow(exportColumns);
    tableHeaderRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      if (colNumber >= 1 && colNumber <= exportColumns.length) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF003366" },
        };
      }
    });

    mapRowsForExport(rows, columns).forEach((row) => {
      worksheet.addRow(row).eachCell({ includeEmpty: true }, (cell) => {
        cell.alignment = { horizontal: "center" };
      });
    });

    try {
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${fileName}.xlsx`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating Excel file:", error);
    }
  };

  const exportToPDF = async () => {
    const rows = await resolveExportData();
    const doc = new jsPDF({ orientation: "landscape" });
    const currentDate = new Date();
    const formattedDate = `${currentDate.getFullYear()}-${currentDate.getMonth() + 1}-${currentDate.getDate()}`;
    const formattedTime = `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;
    const downloadDateTime = `${reportTitle} Generated on: ${formattedDate} ${formattedTime}`;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(`${reportTitle} List`, 14, 20);
    doc.setFontSize(10);
    doc.text(downloadDateTime, 14, 28);

    autoTable(doc, {
      startY: 35,
      head: [exportColumns],
      body: mapRowsForExport(rows, columns),
      headStyles: {
        fillColor: [0, 51, 102],
        textColor: [255, 255, 255],
        fontSize: 8,
        halign: "center",
      },
      bodyStyles: {
        fontSize: 7,
        halign: "center",
      },
    });

    doc.save(`${fileName}.pdf`);
  };

  return (
    <>
      <button type="button" onClick={exportToExcel} className="button-container ms-3">
        <i className="fa-solid fa-file-excel me-1 icon-container"></i> Excel
      </button>
      <button type="button" onClick={exportToPDF} className="button-container ms-3">
        <i className="fa-solid fa-file-pdf me-1 icon-container"></i> PDF
      </button>
    </>
  );
};
